import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';

// ── Voice agents (/voice-agents) ─────────────────────────────────────

export type VoiceAgentRow = {
  id: string;
  name: string;
  category: string;
  icon: string;
  bgColor: string;
  status: string;
  sessions: string;
  bookRate: string;
  costPerAppt: string;
  tags: string[];
  docs: number;
  urls: number;
  raw?: any;
};

/**
 * Maps raw API response from `/api/agents` (or legacy `/api/campaigns`) to VoiceAgentRow.
 */
export function mapVoiceAgent(data: any): VoiceAgentRow {
  const objectiveKey = (data.agent_objective ?? data.objective ?? 'qualify') as string;
  const objectiveMap: Record<string, string> = {
    book: 'Book Appointment',
    book_appointment: 'Book Appointment',
    qualify: 'Qualify Lead',
    transfer: 'Transfer',
    faq: 'FAQ',
    collect: 'Collect',
  };

  const verticalMap: Record<string, { label: string; icon: string; color: string }> = {
    home_services: { label: 'Home Services', icon: 'solar:home-2-bold', color: '#FEF3C7' },
    healthcare: { label: 'Healthcare', icon: 'solar:hospital-bold', color: '#ECFDF5' },
    financial: { label: 'Financial', icon: 'solar:chart-square-bold', color: '#EEF3FF' },
    automotive: { label: 'Automotive', icon: 'gravity-ui:car', color: '#F0FDF4' },
    real_estate: { label: 'Real Estate', icon: 'solar:home-smile-bold', color: '#FFFBEB' },
    general: { label: 'General', icon: 'solar:settings-bold', color: '#F1F5F9' },
  };

  const vertical = (data.vertical as string) || 'general';
  const vInfo = verticalMap[vertical] || { label: vertical || 'Any Industry', icon: 'solar:settings-bold', color: '#F1F5F9' };
  const objLabel = objectiveMap[objectiveKey] || 'Voice Agent';

  const toolTagMap: Record<string, string> = {
    book_appointment: 'Booking',
    send_followup_sms: 'SMS',
    capture_contact: 'CRM',
    transfer_to_human: 'Transfer',
    schedule_callback: 'Callback',
    quote_or_estimate: 'Quote',
    update_crm: 'CRM Sync',
    lookup_customer: 'Lookup',
    get_slots_lookup: 'Availability',
    push_url: 'Push URL',
    service_area: 'Service Area',
    send_email: 'Email',
  };

  const tags = (data.tools_enabled || [])
    .map((t: string) => toolTagMap[t])
    .filter(Boolean);

  const guardrailsObj = typeof data.guardrails === 'object' && data.guardrails !== null ? data.guardrails : {};
  const bannedTopicsRaw = data.banned_topics ?? guardrailsObj.banned_topics;
  const bannedTopics = Array.isArray(bannedTopicsRaw)
    ? bannedTopicsRaw.join(', ')
    : (typeof bannedTopicsRaw === 'string' ? bannedTopicsRaw : '');
  const recordingDisclosure = data.recording_disclosure
    ?? guardrailsObj.recording_disclosure
    ?? data.default_recording_disclosure
    ?? '';
  const guardrails = {
    ...guardrailsObj,
    banned_topics: bannedTopics,
    recording_disclosure: recordingDisclosure || 'This call may be recorded for quality and training purposes.',
  };

  const raw = {
    ...data,
    agent_objective: data.agent_objective ?? (objectiveKey === 'book_appointment' ? 'book' : objectiveKey),
    vertical: data.vertical ?? vertical,
    agent_greeting: data.agent_greeting ?? data.greeting ?? '',
    agent_voice: data.agent_voice ?? data.voice_name ?? '',
    language: data.language ?? 'en_us',
    banned_topics: bannedTopics,
    recording_disclosure: recordingDisclosure || 'This call may be recorded for quality and training purposes.',
    guardrails,
  };

  const rawIcon = (data as any).icon;
  const icon = typeof rawIcon === 'string' && rawIcon.includes(':') ? rawIcon : vInfo.icon;

  return {
    id: data.id,
    name: data.name || 'Untitled Agent',
    category: `${objLabel} · ${vInfo.label}`,
    icon,
    bgColor: (data as any).bgColor || vInfo.color,
    status: data.status || 'draft',
    sessions: String(data.volume ?? data.session_count ?? 0),
    bookRate: (data as any).bookRate ?? (data as any).book_rate ?? '—',
    costPerAppt: (data as any).costPerAppt ?? (data as any).cost_per_appt ?? '—',
    tags: tags.length > 0 ? tags : ['Active'],
    docs: Number(data.docs ?? data.num_docs ?? data.docs_count ?? 0),
    urls: Number(data.urls ?? data.num_urls ?? data.urls_count ?? 0),
    raw,
  };
}

export async function fetchVoiceAgents(axiosInstance?: AxiosInstance, params?: { days?: number; campaign_id?: string }): Promise<VoiceAgentRow[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = apiConfig.voiceEndpoints.agents;
  const response = await client.get<any[]>(url, params ? { params } : undefined);
  return (response.data || []).map(mapVoiceAgent);
}

export type CreateVoiceAgentInput = { name?: string; category?: string };

export async function createVoiceAgent(input: CreateVoiceAgentInput = {}, axiosInstance?: AxiosInstance): Promise<VoiceAgentRow> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = apiConfig.voiceEndpoints.agents;
  const vertical = (input.category as string) || 'general';
  const response = await client.post<any>(url, {
    name: input.name || 'Untitled Agent',
    vertical,
    objective: 'qualify',
    emoji: '🤖',
    tone: ['professional'],
    voice_name: 'Sarah',
    voice_model: 'eleven_turbo_v2_5',
    pace: 3,
    knowledge_categories: [],
  });
  return mapVoiceAgent(response.data);
}

/** Map UI / `raw` fields to `AgentUpdate`-compatible body (only known keys are sent). */
export function buildAgentUpdateBody(patch: Partial<Omit<VoiceAgentRow, 'id'>>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (patch.name != null) body.name = patch.name;
  if (patch.status != null) body.status = patch.status;
  const raw = patch.raw;
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>;
    if (r.vertical != null) body.vertical = r.vertical;
    const obj = String(r.agent_objective ?? r.objective ?? '').trim();
    if (obj === 'book' || obj === 'book_appointment') body.objective = 'book_appointment';
    else if (['qualify', 'transfer', 'faq', 'collect'].includes(obj)) body.objective = obj;
    if (r.persona != null) body.persona = r.persona;
    if (r.greeting != null || r.agent_greeting != null) body.greeting = r.greeting ?? r.agent_greeting;
    if (typeof r.system_prompt === 'string' && r.system_prompt.trim() !== '') body.system_prompt = r.system_prompt;
    if (Array.isArray(r.tone)) body.tone = r.tone;
    if (r.voice_name != null || r.agent_voice != null) body.voice_name = r.voice_name ?? r.agent_voice;
    if (r.voice_model != null) body.voice_model = r.voice_model;
    if (typeof r.pace === 'number' || typeof r.agent_pacing === 'number') body.pace = r.pace ?? r.agent_pacing;
    if (r.language != null) body.language = r.language;
    if (Array.isArray(r.tools_enabled)) body.tools_enabled = r.tools_enabled;
    if (Array.isArray(r.knowledge_categories)) body.knowledge_categories = r.knowledge_categories;
    if (r.emoji != null) body.emoji = r.emoji;

    const guardrailsObj = typeof r.guardrails === 'object' && r.guardrails !== null ? (r.guardrails as Record<string, unknown>) : {};
    const bannedTopics = r.banned_topics ?? guardrailsObj.banned_topics;
    const recordingDisclosure = r.recording_disclosure ?? guardrailsObj.recording_disclosure;

    if (bannedTopics != null) body.banned_topics = bannedTopics;
    if (recordingDisclosure != null) body.recording_disclosure = recordingDisclosure;
    if (r.guardrails != null || bannedTopics != null || recordingDisclosure != null) {
      body.guardrails = {
        ...guardrailsObj,
        ...(bannedTopics != null ? { banned_topics: bannedTopics } : {}),
        ...(recordingDisclosure != null ? { recording_disclosure: recordingDisclosure } : {}),
      };
    }
  }
  return body;
}

export async function updateVoiceAgent(id: string, patch: Partial<Omit<VoiceAgentRow, 'id'>>, axiosInstance?: AxiosInstance): Promise<VoiceAgentRow | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const agents = apiConfig.voiceEndpoints.agents;
  const body = buildAgentUpdateBody(patch);
  if (Object.keys(body).length === 0) {
    const cur = await client.get<any>(`${agents}/${id}`);
    return mapVoiceAgent(cur.data);
  }
  const response = await client.put<any>(`${agents}/${id}`, body);
  return mapVoiceAgent(response.data);
}

export async function deleteVoiceAgent(id: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.agents}/${id}`;
  const response = await client.delete(url);
  return response.status === 204 || response.status === 200;
}

/** `POST /api/agents/bulk-delete` — one round trip instead of N individual DELETEs. */
export async function bulkDeleteVoiceAgents(
  ids: string[], axiosInstance?: AxiosInstance
): Promise<{ deleted: number; failed: Array<{ agent_id: string; error: string }> }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post(`${apiConfig.voiceEndpoints.agents}/bulk-delete`, { agent_ids: ids });
  return response.data;
}

/** Create (optional) then mark live — `POST /api/agents/{agent_id}/publish` per Scan-to-Voice OpenAPI. */
export async function deployVoiceAgent(opts: { id?: string | null; name?: string; config?: Partial<VoiceAgentRow> }, axiosInstance?: AxiosInstance): Promise<VoiceAgentRow> {

  const client = axiosInstance || getVoiceFallbackClient();
  const agents = apiConfig.voiceEndpoints.agents;
  if (opts.id) {
    if (opts.config) {
      await updateVoiceAgent(opts.id, opts.config, axiosInstance);
    }
    const url = `${agents}/${opts.id}/publish`;
    const response = await client.post<any>(url, {});
    return mapVoiceAgent(response.data);
  }
  const body = buildAgentUpdateBody(opts.config || {});
  const createRes = await client.post<any>(agents, {
    name: opts.name || opts.config?.name || 'Untitled Agent',
    vertical: body.vertical || 'general',
    objective: body.objective || 'book_appointment',
    emoji: body.emoji || '🤖',
    tone: body.tone || ['professional'],
    voice_name: body.voice_name || 'Sarah',
    voice_model: body.voice_model || 'eleven_turbo_v2_5',
    pace: body.pace ?? 3,
    greeting: body.greeting,
    system_prompt: body.system_prompt,
    tools_enabled: body.tools_enabled || [],
    knowledge_categories: body.knowledge_categories || [],
    guardrails: body.guardrails,
    banned_topics: body.banned_topics,
    recording_disclosure: body.recording_disclosure,
  });

  const created = createRes.data;
  const pub = await client.post<any>(`${agents}/${created.id}/publish`, {});
  return mapVoiceAgent(pub.data);
}

/** 
 * Test an agent via chat interface.
 * `POST /api/agents/{agent_id}/test-chat`
 */
export async function testVoiceAgentChat(agentId: string, message: string, axiosInstance?: AxiosInstance): Promise<string> {
  const client = axiosInstance || getVoiceFallbackClient();
  const agents = apiConfig.voiceEndpoints.agents;
  const url = `${agents}/${agentId}/test-chat`;
  
  // We expect { "message": "..." } and return { "reply": "..." }
  const response = await client.post<{ reply: string }>(url, { message });
  return response.data?.reply || "No response from agent.";
}

// ── Agent extras ──────────────────────────────────────────────────────

/** `POST /api/agents/{agent_id}/pause` — pause a live agent. */
export async function pauseVoiceAgent(id: string, axiosInstance?: AxiosInstance): Promise<VoiceAgentRow> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`${apiConfig.voiceEndpoints.agents}/${id}/pause`, {});
  return mapVoiceAgent(response.data);
}

/** `POST /api/agents/{agent_id}/duplicate` — duplicate an agent. */
export async function duplicateVoiceAgent(id: string, axiosInstance?: AxiosInstance): Promise<VoiceAgentRow> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`${apiConfig.voiceEndpoints.agents}/${id}/duplicate`, {});
  return mapVoiceAgent(response.data);
}
